import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { podcastAPI, episodeAPI, APIError } from "../../lib/api";
import type { PodcastResponse, EpisodeResponse } from "../../types/api";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface PodcastAnalyticsData {
  podcast: PodcastResponse;
  organizationId: string;
}

export async function podcastAnalyticsLoader({
  params,
}: LoaderFunctionArgs): Promise<PodcastAnalyticsData> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId || !isValidUUID(orgId) || !isValidUUID(podcastId)) {
    throw redirect("/organizations");
  }

  try {
    const podcast = await podcastAPI.getById(orgId, podcastId);
    return { podcast, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts`);
    }
    throw error;
  }
}

export interface EpisodeAnalyticsData {
  episode: EpisodeResponse;
  podcast: PodcastResponse;
  organizationId: string;
}

export async function episodeAnalyticsLoader({
  params,
}: LoaderFunctionArgs): Promise<EpisodeAnalyticsData> {
  const { id: orgId, podcastId, episodeId } = params;

  if (
    !orgId ||
    !podcastId ||
    !episodeId ||
    !isValidUUID(orgId) ||
    !isValidUUID(podcastId) ||
    !isValidUUID(episodeId)
  ) {
    throw redirect("/organizations");
  }

  try {
    const [podcast, episode] = await Promise.all([
      podcastAPI.getById(orgId, podcastId),
      episodeAPI.getById(orgId, podcastId, episodeId),
    ]);
    return { episode, podcast, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/podcasts/${podcastId}`);
    }
    throw error;
  }
}
